import React from 'react';
import Translate from '@spordle/intl-elements';
import { useIntl } from 'react-intl';
import cn from 'classnames';

import Team from '../Team';
import { ProfileRow } from '../Profile';
import { SchemaStatsHeader, SchemaStatsRow } from '../SchemaStats';
import { getStatuses } from '../../lib';

const PLAYER_POSITIONS = ['F', 'C', 'D'];

const PlayerStatus = ({ player }) => {
  const { formatMessage } = useIntl();

  const statuses = getStatuses(player.stats);
  if (!statuses.length) return null;
  return <span className="text-muted">
    ({statuses.map(status => formatMessage({ id: `common.enums.playerstatus.${status}` })).join(' / ')})
  </span>
}

const PlayerPositions = ({ positions }) => {
  const { formatMessage } = useIntl();

  const playerPositions = positions?.length && PLAYER_POSITIONS.filter(position => positions.includes(position));
  if (!playerPositions?.length) return null;

  return <span>{playerPositions.map(position => formatMessage({ id: `common.enums.positions.${position}.abbr` })).join(', ')}</span>
}

export const TableHeader = ({ isMobile, isSingle, properties, hidePositions, hideRank, disableSorting, querySort, queryOrder, handleSorting }) => {
  const { formatMessage } = useIntl();

  return isMobile ?
    <tr>
      {!hideRank && <th className="small font-weight-bold text-center"><Translate id="common.lang.rank.abbr" /></th>}
      <th className="small font-weight-bold">#</th>
      <th className="small font-weight-bold"><Translate id="common.lang.player" /></th>
      {!isSingle && <th className="small font-weight-bold text-center"><Translate id="common.lang.team" /></th>}
      {!hidePositions && <th className="small font-weight-bold text-center"><Translate id="common.lang.position.abbr" /></th>}
      <SchemaStatsHeader
        properties={properties}
        className="small font-weight-bold text-center"
        disableSorting={disableSorting}
        querySort={querySort}
        queryOrder={queryOrder}
        handleSorting={handleSorting}
      />
    </tr>
    :
    <tr>
      {!hideRank && <th className="text-center" title={formatMessage({ id: 'common.lang.rank' })}>
        <Translate id="common.lang.rank.abbr" />
      </th>}
      <th className="text-center" title={formatMessage({ id: 'common.lang.jersey' })}>
        #
      </th>
      <th style={{ minWidth: 150 }}><Translate id="common.lang.player" /></th>
      {!isSingle && <th><Translate id="common.lang.team" /></th>}
      {!hidePositions && <th className="text-center" title={formatMessage({ id: 'common.lang.position' })}>
        <Translate id="common.lang.position.abbr" />
      </th>}
      <SchemaStatsHeader
        properties={properties}
        className="text-center"
        disableSorting={disableSorting}
        querySort={querySort}
        queryOrder={queryOrder}
        handleSorting={handleSorting}
      />
    </tr>
}

const StatsRow = ({ isMobile, player, index, isSingle, profileOpen, showGroup = false, toggle, properties, hidePositions, hideRank, querySort }) => {
  const classes = cn('row-player', { 'has-profile': player.profile, 'profile-open': profileOpen })
  const hideGroup = !player?.group || !showGroup;
  const unranked = querySort ? '-' : null;

  return isMobile ?
    <tr className={classes} onClick={toggle}>
      {!hideRank && <td className="align-middle font-weight-semibold text-large text-center">{player.stats.ranking || unranked || index + 1}</td>}
      <td className={cn("align-middle text-center", { 'font-weight-semibold': hideRank })}>{player.stats.number ? player.stats.number : null}</td>
      <td className="small align-middle">
        {player.fullName?.toUpperCase()} <PlayerStatus player={player} />
        {!hideGroup && <div className="small">{player.group.name}</div>}
      </td>
      {!isSingle && <td className="small align-middle">
        <Team team={player.team} scheduleId={player.stats.scheduleId} groupId={player.stats.groupId} />
      </td>}
      {!hidePositions && <td className="small align-middle text-center"><PlayerPositions positions={player.stats.positions} /></td>}
      <SchemaStatsRow
        properties={properties}
        stats={player.stats}
        className="small align-middle text-center"
      />
    </tr>
    :
    <tr className={classes} onClick={toggle}>
      {!hideRank && <td className="align-middle font-weight-semibold text-large text-center">{player.stats.ranking || unranked || index + 1}</td>}
      <td className={cn("align-middle text-center", { 'font-weight-semibold': hideRank })}>{player.stats.number ? player.stats.number : null}</td>
      <td className={cn('align-middle', { 'font-weight-bold': profileOpen })}>
        {player.fullName?.toUpperCase()} <PlayerStatus player={player} />
        {!hideGroup && <div className="small">{player.group.name}</div>}
      </td>
      {!isSingle && <td className="align-middle">
        <Team team={player.team} scheduleId={player.stats.scheduleId} groupId={player.stats.groupId} />
      </td>}
      {!hidePositions && <td className="align-middle text-center"><PlayerPositions positions={player.stats.positions} /></td>}
      <SchemaStatsRow
        properties={properties}
        stats={player.stats}
        className="align-middle text-center"
      />
    </tr>
}

export const TableRow = props => {
  const { properties = [], isSingle, hidePositions, hideRank } = props;
  const baseColumns = (hideRank ? -1 : 0) /* rank */ + 2 /* jersey, name */ + (isSingle ? 0 : 1) /* team */ + (hidePositions ? 0 : 1) /* position */
  const totalColumns = baseColumns + properties.length

  // offset deals with the profile hiding behind the scroll
  const colSpan = isSingle ? Math.min(12, totalColumns) : totalColumns;
  const offsetColSpan = isSingle ? totalColumns - colSpan : 0;

  return <ProfileRow colSpan={colSpan} offsetColSpan={offsetColSpan} {...props}>
    <StatsRow />
  </ProfileRow>
}
